import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as api from '../../api/PurchaseAPI/newStockReceivedAPI';

const getErrorMessage = (err, fallback) =>
    err?.ErrorDetails?.ErrorMessage || err?.Message || err?.message || fallback;

// ── Async Thunks ─────────────────────────────────────────────────────────────

export const fetchCostCenters = createAsyncThunk(
    'newStockReceived/fetchCostCenters',
    async ({ uid, rid }, { rejectWithValue }) => {
        try {
            const res = await api.getNewStockIssueReceivedCCs(uid, rid);
            return res?.Data || [];
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to load cost centers'));
        }
    }
);

export const fetchIndents = createAsyncThunk(
    'newStockReceived/fetchIndents',
    async (CCCode, { rejectWithValue }) => {
        try {
            const res = await api.getIssueRecIndentsbyCC(CCCode);
            return res?.Data || [];
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to load indents'));
        }
    }
);

export const fetchPreviewGrid = createAsyncThunk(
    'newStockReceived/fetchPreviewGrid',
    async ({ ccode, indent }, { rejectWithValue }) => {
        try {
            const res = await api.getNewItemsIssueReceivedDetails(ccode, indent);
            return res?.Data || [];
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to load items'));
        }
    }
);

export const fetchExpDate = createAsyncThunk(
    'newStockReceived/fetchExpDate',
    async (indentno, { rejectWithValue }) => {
        try {
            const res = await api.getNewStockReceivedExpDate(indentno);
            const data = res?.Data;
            const record = Array.isArray(data) ? data[0] : data;
            return record?.Expdate || '';
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to load expected date'));
        }
    }
);

// Success format is "Submited,<RefNo>".
export const submitReceived = createAsyncThunk(
    'newStockReceived/submit',
    async (payload, { rejectWithValue }) => {
        try {
            const res = await api.saveNewItemsIssueReceived(payload);
            const raw = res?.Data || '';
            const [status, refNo] = raw.split(',');
            if (status !== 'Submited') {
                return rejectWithValue(raw || 'Failed to submit');
            }
            return { refNo };
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to submit'));
        }
    }
);

// ── Slice ────────────────────────────────────────────────────────────────────

const initialState = {
    ccList: [],
    indentList: [],
    previewItems: [],
    expDate: '',
    submitResult: null,
    loading: {
        ccList: false,
        indentList: false,
        previewGrid: false,
        expDate: false,
        submit: false,
    },
    errors: {
        ccList: null,
        indentList: null,
        previewGrid: null,
        expDate: null,
        submit: null,
    },
};

const newStockReceivedSlice = createSlice({
    name: 'newStockReceived',
    initialState,
    reducers: {
        clearIndents:     (s) => { s.indentList = []; },
        clearPreviewGrid: (s) => { s.previewItems = []; s.expDate = ''; s.errors.previewGrid = null; },
        clearSubmitResult:(s) => { s.submitResult = null; s.errors.submit = null; },
        resetNewStockReceived: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCostCenters.pending,   (s) => { s.loading.ccList = true; s.errors.ccList = null; })
            .addCase(fetchCostCenters.fulfilled, (s, a) => { s.loading.ccList = false; s.ccList = a.payload; })
            .addCase(fetchCostCenters.rejected,  (s, a) => { s.loading.ccList = false; s.errors.ccList = a.payload; });

        builder
            .addCase(fetchIndents.pending,   (s) => { s.loading.indentList = true; s.errors.indentList = null; })
            .addCase(fetchIndents.fulfilled, (s, a) => { s.loading.indentList = false; s.indentList = a.payload; })
            .addCase(fetchIndents.rejected,  (s, a) => { s.loading.indentList = false; s.errors.indentList = a.payload; });

        builder
            .addCase(fetchPreviewGrid.pending,   (s) => { s.loading.previewGrid = true; s.errors.previewGrid = null; })
            .addCase(fetchPreviewGrid.fulfilled, (s, a) => { s.loading.previewGrid = false; s.previewItems = a.payload; })
            .addCase(fetchPreviewGrid.rejected,  (s, a) => { s.loading.previewGrid = false; s.errors.previewGrid = a.payload; s.previewItems = []; });

        builder
            .addCase(fetchExpDate.pending,   (s) => { s.loading.expDate = true; })
            .addCase(fetchExpDate.fulfilled, (s, a) => { s.loading.expDate = false; s.expDate = a.payload; })
            .addCase(fetchExpDate.rejected,  (s) => { s.loading.expDate = false; s.expDate = ''; });

        builder
            .addCase(submitReceived.pending,   (s) => { s.loading.submit = true; s.errors.submit = null; s.submitResult = null; })
            .addCase(submitReceived.fulfilled, (s, a) => { s.loading.submit = false; s.submitResult = a.payload; s.previewItems = []; })
            .addCase(submitReceived.rejected,  (s, a) => { s.loading.submit = false; s.errors.submit = a.payload; });
    },
});

export const {
    clearIndents,
    clearPreviewGrid,
    clearSubmitResult,
    resetNewStockReceived,
} = newStockReceivedSlice.actions;

// ── Selectors ────────────────────────────────────────────────────────────────
export const selectNSRCCList       = (s) => s.newStockReceived.ccList;
export const selectNSRIndentList   = (s) => s.newStockReceived.indentList;
export const selectNSRPreviewItems = (s) => s.newStockReceived.previewItems;
export const selectNSRExpDate      = (s) => s.newStockReceived.expDate;
export const selectNSRSubmitResult = (s) => s.newStockReceived.submitResult;
export const selectNSRLoading      = (s) => s.newStockReceived.loading;
export const selectNSRErrors       = (s) => s.newStockReceived.errors;

export default newStockReceivedSlice.reducer;
